/**
 * PDP Add to Cart Button Web Component
 * Handles add to cart functionality with loading states for product forms
 */

export default class PDPAddToCartButton extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.submitButton = null;

    this.defaultText = '';
    this.addingText = '';

    this.isSubmitting = false;

    this.boundHandleSubmit = this.handleSubmit.bind(this);
    this.boundRestoreButtonState = this.restoreButtonState.bind(this);
    this.boundReinit = this.reinit.bind(this);

    this._formRef = null;
    this._variantListenerAdded = false;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.form = this.closest('form') || this.querySelector('form');

    if (!this.form) {
      console.warn('PDPAddToCartButton: No form found');
      return;
    }

    this._formRef = this.form;

    this.submitButton = this.form.querySelector('[type="submit"]');

    if (!this.submitButton) {
      console.warn('PDPAddToCartButton: No submit button found');
      return;
    }

    this.defaultText =
      this.submitButton.getAttribute('data-add-to-cart-text') ||
      this.readLabel() ||
      window.Theme?.translations?.add_to_cart ||
      'Add to cart';

    this.addingText =
      this.submitButton.getAttribute('data-adding-text') ||
      window.Theme?.translations?.adding_to_cart ||
      'Adding...';

    this.form.addEventListener('submit', this.boundHandleSubmit);

    document.addEventListener(
      'cart:updated',
      this.boundRestoreButtonState,
      { passive: true }
    );

    if (!this._variantListenerAdded) {
      document.addEventListener(
        'variant:loaded',
        this.boundReinit,
        { passive: true }
      );
      this._variantListenerAdded = true;
    }
  }

  cleanup() {
    if (this._formRef) {
      this._formRef.removeEventListener('submit', this.boundHandleSubmit);
    }

    document.removeEventListener(
      'cart:updated',
      this.boundRestoreButtonState
    );

    document.removeEventListener(
      'variant:loaded',
      this.boundReinit
    );

    this._variantListenerAdded = false;
  }

  reinit() {
    if (this.isSubmitting) return;

    const newForm = this.closest('form') || this.querySelector('form');

    if (!newForm) return;

    if (newForm === this._formRef) return;

    if (this._formRef) {
      this._formRef.removeEventListener('submit', this.boundHandleSubmit);
    }

    this.form = newForm;
    this._formRef = newForm;

    this.submitButton = this.form.querySelector('[type="submit"]');

    if (!this.submitButton) return;

    this.defaultText =
      this.submitButton.getAttribute('data-add-to-cart-text') ||
      this.readLabel() ||
      window.Theme?.translations?.add_to_cart ||
      'Add to cart';

    this.addingText =
      this.submitButton.getAttribute('data-adding-text') ||
      window.Theme?.translations?.adding_to_cart ||
      'Adding...';

    this.form.addEventListener('submit', this.boundHandleSubmit);
  }

  /**
   * Read button label
   */
  readLabel() {
    if (!this.submitButton) return '';

    if (this.submitButton.tagName === 'INPUT') {
      return this.submitButton.value;
    }

    const textSpan = this.submitButton.querySelector('.plp-card__button-text');

    if (textSpan) {
      return textSpan.textContent.trim();
    }

    return this.submitButton.textContent.trim();
  }

  /**
   * Write button label
   */
  writeLabel(text) {
  if (!this.submitButton) return;

  const icon = this.submitButton.querySelector('svg');
  const iconHTML = icon ? icon.outerHTML : '';

  // NEW: detect icon-only mode
  const iconOnly = this.submitButton.dataset.iconOnly === 'true';

  // If icon only → show only svg
  if (iconOnly) {
    this.submitButton.innerHTML = iconHTML;
    return;
  }

  const priceSpan =
    this.submitButton.querySelector('.plp-card__button-price');

  const priceHTML = priceSpan ? priceSpan.outerHTML : '';

  const isIconFirst =
    this.submitButton.classList.contains('icon-first');

  this.submitButton.innerHTML = '';

  const textWrapper = document.createElement('span');

  textWrapper.className =
    'plp-card__button-text inline-flex items-center gap-1';

  textWrapper.textContent = text;

  if (priceHTML) {
    textWrapper.insertAdjacentHTML('beforeend', priceHTML);
  }

  if (isIconFirst && iconHTML) {
    this.submitButton.insertAdjacentHTML('beforeend', iconHTML);
  }

  this.submitButton.appendChild(textWrapper);

  if (!isIconFirst && iconHTML) {
    this.submitButton.insertAdjacentHTML('beforeend', iconHTML);
  }
}

  setFormInteractionState(disabled) {
    if (!this.form) return;

    const controls = this.form.querySelectorAll(
      'variant-picker input, variant-picker select, variant-picker button, quantity-input input, quantity-input button'
    );

    controls.forEach((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLButtonElement)) {
        return;
      }

      if (disabled) {
        if (!control.hasAttribute('data-was-disabled')) {
          control.setAttribute(
            'data-was-disabled',
            control.disabled ? 'true' : 'false'
          );
        }
        control.disabled = true;
        return;
      }

      const wasDisabled = control.getAttribute('data-was-disabled') === 'true';
      control.disabled = wasDisabled;
      control.removeAttribute('data-was-disabled');
    });

    const variantPicker = this.form.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }
  }

  restoreButtonState() {
    if (this.isSubmitting) return;

    if (!this.submitButton) return;

  const isUnavailable =
      this.form?.hasAttribute('disabled') ||
      this.submitButton.dataset.variantAvailable === 'false';

    if (isUnavailable) {
      const comingSoonEnabled = this.submitButton.dataset.comingSoonEnabled === 'true';

      if (comingSoonEnabled) {
        this.writeLabel(
          this.submitButton.getAttribute('data-coming-soon-text') || 'Coming Soon'
        );
        this.submitButton.type = 'button';
        this.submitButton.classList.add('js-open-coming-soon');
        this.submitButton.disabled = false;
      } else {
        this.writeLabel(
          this.submitButton.getAttribute('data-sold-out-text') ||
          (window.Theme?.translations?.sold_out || 'Sold out')
        );
        this.submitButton.type = 'submit';
        this.submitButton.classList.remove('js-open-coming-soon');
        this.submitButton.disabled = true;
      }
    } else {
      this.submitButton.type = 'submit';
      this.submitButton.classList.remove('js-open-coming-soon');
      const host = this.form?.querySelector('[data-inventory-tracked]') ||
        this.querySelector('[data-inventory-tracked]');
      const tracked = host && host.dataset.inventoryTracked === 'true';
      const inventory = host ? parseInt(host.dataset.variantInventory || '0', 10) : 0;
      const inCart = host ? parseInt(host.dataset.inCart || '0', 10) : 0;

      this.writeLabel(this.defaultText);

      if (tracked && inventory > 0 && inCart >= inventory) {
        this.submitButton.disabled = true;
        this.showInlineMsg(
          `Already at max stock in cart (${inCart} of ${inventory})`,
          true
        );
      } else {
        this.submitButton.disabled = false;
        this.showInlineMsg('');
      }
    }

    this.submitButton.removeAttribute('aria-busy');
  }

  /**
   * Compare at price logic
   */
  getSelectedCompareAtPrice() {
    if (!this.form) return null;

    const selectedRadio =
      this.form.querySelector('.js-pdp-qty-breaks:checked');

    if (selectedRadio && selectedRadio.dataset.comparePrice) {
      return selectedRadio.dataset.comparePrice;
    }

    return null;
  }

  updateCompareAtPrice() {
    if (!this.form) return;

    const comparePrice = this.getSelectedCompareAtPrice();

    const compareInput =
      this.form.querySelector(
        'input[name="properties[compare_at_price]"]'
      );

    if (compareInput && comparePrice !== null) {
      compareInput.value = comparePrice;
    }
  }

  /**
   * Read variant inventory attrs from form context
   */
  getInventoryInfo() {
    const host = this.form.querySelector('[data-inventory-tracked]') ||
      this.querySelector('[data-inventory-tracked]');
    if (!host) return { tracked: false, inventory: 0 };
    return {
      tracked: host.dataset.inventoryTracked === 'true',
      inventory: parseInt(host.dataset.variantInventory || '0', 10),
    };
  }

  async getInCartQty(variantId) {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const vid = String(variantId);
      return (cart.items || [])
        .filter(i => String(i.variant_id) === vid)
        .reduce((s, i) => s + (i.quantity || 0), 0);
    } catch (err) {
      return 0;
    }
  }

  showInlineMsg(text, persistent = false) {
    const msgEl = this.form.querySelector('.pdp__inventory-msg') ||
      this.querySelector('.pdp__inventory-msg');
    if (!msgEl) return;
    if (!text) {
      msgEl.classList.add('hidden');
      msgEl.textContent = '';
      return;
    }
    msgEl.textContent = text;
    msgEl.classList.remove('hidden');
    clearTimeout(this._msgTimeout);
    if (!persistent) {
      this._msgTimeout = setTimeout(() => {
        msgEl.classList.add('hidden');
        msgEl.textContent = '';
      }, 4000);
    }
  }

  /**
   * Submit handler
   */
  async handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.isSubmitting) return;

    if (!this.submitButton || !this.form) return;

    this.isSubmitting = true;

    const errorMessage =
      this.form.querySelector('.product-form-text__error');

    this.updateCompareAtPrice();

    const formData = new FormData(this.form);
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');
    this.setFormInteractionState(true);

    const { tracked, inventory } = this.getInventoryInfo();
    const qtyInput = this.form.querySelector('input[name="quantity"]');
    const variantId = formData.get('id');
    let requestedQty = parseInt(formData.get('quantity'), 10) || 1;

    if (tracked && variantId) {
      const inCart = await this.getInCartQty(variantId);
      const remaining = inventory - inCart;

      if (remaining <= 0) {
        this.isSubmitting = false;
        if (inventory > 0 && inCart >= inventory) {
          this.showInlineMsg(
            `Already at max stock in cart (${inCart} of ${inventory})`,
            true
          );
        } else {
          this.showInlineMsg('');
        }
        this.setFormInteractionState(false);
        this.restoreButtonState();
        return;
      }

      if (requestedQty > remaining) {
        requestedQty = remaining;
        if (qtyInput) qtyInput.value = String(requestedQty);
        this.showInlineMsg(
          `Only ${requestedQty} added (${inCart} already in cart, ${inventory} in stock)`
        );
      }
    }

    this.writeLabel(this.addingText);

    if (variantId !== null) {
      formData.set('id', String(variantId));
    }
    if (tracked && qtyInput) formData.set('quantity', String(requestedQty));

    try {
      const response = await fetch(
        Shopify.routes.root + 'cart/add.js',
        {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (response.status === 429) {
        throw new Error(
          'Too many requests. Please try again.'
        );
      }

      const data = await response.json();

      if (!response.ok) {
        this.showError(
          data.message || 'Failed to add to cart',
          errorMessage
        );
        throw new Error(data.message);
      }

      if (typeof window.getCart === 'function') {
        await window.getCart();
      }

      if (typeof window.toggleMiniCart === 'function') {
        window.toggleMiniCart('open');
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated')
      );

      this.dispatchEvent(
        new CustomEvent('pdp:added-to-cart', {
          bubbles: true,
          detail: {
            form: this.form,
            button: this.submitButton,
          },
        })
      );
    } catch (err) {
      console.error('Add to cart error:', err);

      this.showError(
        err.message || 'Failed to add to cart',
        errorMessage
      );

      this.dispatchEvent(
        new CustomEvent('pdp:add-to-cart-error', {
          bubbles: true,
          detail: {
            error: err,
            form: this.form,
            button: this.submitButton,
          },
        })
      );
    } finally {
      this.isSubmitting = false;
      this.setFormInteractionState(false);
      this.restoreButtonState();
    }
  }

  showError(message, errorEl) {
    if (!message) return;

    if (errorEl) {
      const msg = errorEl.querySelector('.error-message');

      if (msg) {
        msg.textContent = message;

        errorEl.classList.remove('hidden');

        clearTimeout(this.errorTimeout);

        this.errorTimeout = setTimeout(() => {
          msg.textContent = '';
          errorEl.classList.add('hidden');
        }, 3000);
      }
    } else {
      console.warn('Add to cart error:', message);
    }
  }
}

if (!customElements.get('pdp-add-to-cart-button')) {
  customElements.define(
    'pdp-add-to-cart-button',
    PDPAddToCartButton
  );
}
